import { MobileApplicationCard } from "features/applications/MobileApplicationCard";
import { LeadDetails } from "../../features/leads/components/LeadDetails";
import { useNavigate, useParams } from "react-router-dom";
import { ApplicationList } from "features/applications/ApplicationList";
import { applicationService } from "features/createApplication/service/applicationService";
import { useEffect, useState } from "react";
import { decodeURIComponent } from "@lib/utils";
import { useIsMobile } from "@customHooks/use-mobile";
import { useToast } from "@customHooks/useToast";
import ProductTypeModal from "features/applications/components/ProductTypeModal";
import { searchLeads } from "features/leads/services/leadsService";
import { PRODUCT_TYPES, ProductType } from "features/applications/config";
import { CardShimmer } from "features/applications/ApplicationShimmer";
import { Button } from "@components/shadcn-ui/button";
import { ApplicationTasks } from "../../features/tasks/components/ApplicationTasks";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/shadcn-ui/tabs";

export const LeadDetailsPage = () => {
  const isMobile = useIsMobile();
  const { id: phoneNumber } = useParams();
  const [applications, setApplications] = useState<any[]>([]);
  const navigate = useNavigate();
  const [productType, setProductType] = useState<string>("");
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isApplicationLoading, setIsApplicationLoading] = useState(true);
  const [lead, setLead] = useState<any | null>(null);
  const decodedLeadId = decodeURIComponent(phoneNumber!).toString();
  const [groupMemberInfo, setGroupMemberInfo] = useState<any | null>(null);

  useEffect(() => {
    if (lead?.metadata) {
      try {
        const metadata = typeof lead.metadata === 'string'
          ? JSON.parse(lead.metadata)
          : lead.metadata;

        if (metadata.memberOf) {
          const memberOf = typeof metadata.memberOf === 'string'
            ? JSON.parse(metadata.memberOf)
            : metadata.memberOf;
          setGroupMemberInfo(memberOf);
        }
      } catch (e) {
        console.error("Error parsing lead metadata:", e);
      }
    }
  }, [lead]);

  useEffect(() => {
    const fetchApplicationById = async () => {
      try {
        const data = await applicationService.getSummaryByLeadId(decodedLeadId);
        console.log("Application Details:", data);
        setApplications(data);
        setIsApplicationLoading(false);
      } catch (err) {
        setIsApplicationLoading(false);
        console.error("Failed to fetch application by ID:", err);
      }
    };

    if (phoneNumber) {
      fetchApplicationById();
    }
  }, [phoneNumber]);

  useEffect(() => {
    const fetchLeadDetails = async () => {
      if (!phoneNumber) return;
      setIsLoading(true);
      try {
        const leads = await searchLeads(undefined, +decodedLeadId);

        if (leads.length > 0) {
          setLead(leads[0]);
        }
      } catch (err) {
        console.log("Failed to fetch lead details");
      } finally {
        setIsLoading(false);
      }
    };

    phoneNumber && fetchLeadDetails();
  }, [phoneNumber]);

  if (!phoneNumber) return;

  const handleCreateApplication = async () => {
    try {
      const payload: any = { productType: productType, ...lead, leadId: decodedLeadId };

      if (productType === "Group Loan" && groupMemberInfo) {
        payload.groupDetails = groupMemberInfo;
      }

      const response: any = await applicationService.performAction(
        payload,
        "CREATE_APPLICATION",
        decodeURIComponent(phoneNumber)
      );
      const applicationId = response.data.outcome.message;

      toast({
        description: `You have initiated an application... with Application Id ${applicationId}`,
      });

      navigate(
        `/dashboard/application/${phoneNumber}?action=edit&id=${applicationId}`
      );
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... could not initiate application",
      });
      console.error("Error in creating application:", error);
    }
  };

  return (
    <div className="h-fit">
      <strong className="block mt-0 mb-2 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm">
        Lead Details
      </strong>
      <LeadDetails isLoading={isLoading} lead={lead} groupMemberInfo={groupMemberInfo} />

      <br />

      <Tabs defaultValue="applications" className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="applications" className="flex-1">
            Applications
          </TabsTrigger>
          <TabsTrigger value="tasks" className="flex-1">
            Tasks
          </TabsTrigger>
        </TabsList>

        <TabsContent value="applications">
          <ProductTypeModal
            onChange={setProductType}
            onSubmit={handleCreateApplication}
            allowedProductTypes={
              !groupMemberInfo
                ? Object.values(PRODUCT_TYPES).filter(t => t !== PRODUCT_TYPES.GROUP_LOAN)
                : undefined
            }
          />
          <div className="mt-4">
            <strong className="block mt-0 mb-2 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm">
              All Applications
            </strong>

            {isApplicationLoading ? (
              <CardShimmer length={3} />
            ) : (
              <>
                {applications.length > 0 ? (
                  <>
                    {!isMobile ? (
                      <ApplicationList applications={applications} />
                    ) : (
                      <MobileApplicationCard applications={applications} />
                    )}
                  </>
                ) : (
                  <div className="flex justify-center">
                    You haven't created any applications.
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>
        <TabsContent value="tasks">
          {phoneNumber && (
            <div className="mb-2 mt-3">
              <div className="flex justify-between items-center bg-[#bbd0ce] p-2 rounded-sm">
                <strong className="text-md">Tasks</strong>
                <Button
                  onClick={() => (navigate(`/dashboard/lead-details/${decodedLeadId}/create-task?resourceType=LEAD_TASK`))}
                  className="bg-primary hover:bg-primary/90 h-8 text-sm"
                >
                  Create Task
                </Button>
              </div>
              <ApplicationTasks resourceId={decodedLeadId} resourceType={"LEAD_TASK"} />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
